import RulesParser from './RulesParser'
import Field from './Field'
import Validators from './rules'
import Errors from '../Errors'
import {isArrayWithObjects, substringIndex} from '@utils'

export default class Validator
{
    constructor( formObject, rules, scenario = 'default', language = 'en', uniqueKeyParam = false, applyDefaultValues = true ) {
        this._form = formObject
        this._language = language
        this._onErrorHooks = new Map()

        this.setUniqueKeyParam(uniqueKeyParam)

        // register all available rules from /rules/index.js
        this.$validators = {}
        Object.keys(Validators).forEach( validatorName => {
            this.$validators[validatorName] = Validators[validatorName]
        })
        //

        this.$errorMessages = require('./messages/'+ this.getLanguage()).default

        this.$schema = new RulesParser(rules).asSchema()

        this.scenario = scenario

        // ( $validator, fieldName ) => {}
        this._beforeValidate = new Map()

        // ( result, $validator, fieldName ) => {}
        this._afterValidate = new Map()

        // init properties
        this.uniqueFieldKeyCache = {}
        this.fieldsState = {}
        this.dirtyFields = []
        this.loadingFields = []
        this.$errors = new Errors()
        this.$errorsSilent = new Errors()
        this.$warnings = new Errors()
        this.$warningsSilent = new Errors()
        //

        if( applyDefaultValues ) {
            this.form().registerAfterInitHandler(form => {
                this.applyDefaultValues()

                if( this.form().getOptions().useSync === true ) {
                    this.sync()
                }
            })
        }

        this.syncUnwatchers = []
    }

    sync()
    {
        // clear old watchers
        for( let index in this.syncUnwatchers ) {
            this.syncUnwatchers[index]()
        }
        this.syncUnwatchers = []
        //

        let schemaFieldsWithSyncRule = this.getSchemaFieldsWithRule('sync')

        this.applyCallbackOnRealFields(schemaFieldsWithSyncRule, (schemaField, realFieldName) => {
            this.$schema[schemaField].setValidator(this).sync( realFieldName )
        })
    }

    applyCallbackOnRealFields( schemaFields, callback ) {

        if( ! isArray(schemaFields) ) {
            schemaFields = [schemaFields]
        }

        let _iterate = ( data, callback, parentKey = undefined ) => {
            Object.keys(data).forEach( field => {

                let value = data[field]

                let fieldName = parentKey === undefined
                                    ? field
                                    : parentKey + '.' + field


                let hasArrayObjectValue = typeOf(value) === 'object' || isArrayWithObjects(value)

                if( ! field.match(/^[0-9]$/) || ! hasArrayObjectValue ) { // skip indexes of arrays with objects
                    callback(fieldName)
                }

                if( hasArrayObjectValue ) {
                    _iterate( value, callback, fieldName )
                }
            })
        }

        schemaFields.forEach(schemaField => {

            if(this.form().has(schemaField)) {
                callback(schemaField, schemaField)
                return // skip the rest of the code
            }

            if( schemaField instanceof RegExp || schemaField.indexOf('/') === 0 )
            {
                let formData = this._form.all()

                for( let formKey in formData )
                {
                    let value = formData[formKey]

                    if(isArrayWithObjects(value) || isObject(value)) {
                        _iterate(value, (fieldName) => {

                            if( this.matchField(schemaField, fieldName) ) {
                                callback(schemaField, fieldName)
                            }

                        }, formKey)
                    }
                }

                return
            }

            schemaField.split('.').reduce((path, current, index) => {
                let key = path
                if( path === undefined ) {
                    key = current
                }

                let value = this.form().get(key)

                if(isArrayWithObjects(value) || isObject(value)) {
                    _iterate(value, (fieldName) => {

                        if( this.matchField(schemaField, fieldName) ) {
                            callback(schemaField, fieldName)
                        }

                    }, key)
                }

                return current
            }, undefined)
        })
    }

    matchField(schemaField, fieldName)
    {
        // match: item.0.id
        if( schemaField === fieldName ) {
            return true
        }

        // match: item.id
        let schemaFieldName = this._getSchemaFieldName(fieldName)
        if( schemaField === schemaFieldName ) {
            return true
        }

        // match: item.*
        let asteriskPath = this._getAsteriskPath(schemaFieldName)
        if( asteriskPath === schemaField ) {
            return true
        }

        // case: regex $(/0-9/) or $(new RegExp('0-9'))

        if (schemaField instanceof RegExp) {
            return schemaField.test(fieldName)
        }

        if( schemaField.indexOf('/') === 0 ) {

            let lastSlash = schemaField.lastIndexOf("/");
            let regex = new RegExp(schemaField.slice(1, lastSlash), schemaField.slice(lastSlash + 1));

            if( regex.test(String(fieldName)) ) {
                return true
            }
        }

        return false
    }

    reset() {
        this.$errors = new Errors()
        this.$errorsSilent = new Errors()

        this.$warnings = new Errors()
        this.$warningsSilent = new Errors()

        this.clearDirtyFields()
        this.clearLoadingFields()
        this.clearUniqueFieldKeyCache()

        return this
    }

    registerFieldsState(clear = false) {
        if( clear ) {
            this.fieldsState = {}
        }

        let scan = ( data, parent ) => {
            for(let key in data) {
                if( typeOf(data[key]) == 'object' || Array.isArray(data[key]) ) {
                    scan(data[key], parent + '.' + key)
                } else {
                    this.registerFieldState(parent + '.' + key)
                }
            }
        }

        this.form().keys().forEach( field => {
            if( typeOf(this.form()[field]) == 'object' || Array.isArray(this.form()[field]) ) {
                scan(this.form()[field], field)
            } else {
                this.registerFieldState(field)
            }
        })
    }

    registerFieldState(fieldName) {
        fieldName = this.getUniqueFieldKey(fieldName)

        if( this.fieldsState.hasOwnProperty(fieldName) ) {
            return
        }

        Vue.set(this.fieldsState, fieldName, {
            isLoading: false,
            isDirty: false
        })
    }

    clearUniqueFieldKeyCache() {
        this.uniqueFieldKeyCache = {}
    }

    /**
     * Check and apply default value for specific field name.
     *
     * @param  {String} key Original field name. Ex.: data.0.title
     */
    applyDefaultValue( key ) {
        let fieldsWithDefaultRule = this.getSchemaFieldsWithRule('default')

        this._loadDefaultValue(key, fieldsWithDefaultRule, !this.form().has(key))

        return this
    }

    /**
     * Scan $schema in order to find fields with default rules set.
     * Extract the default value and apply it based on scenario and when conditions.
     */
    applyDefaultValues() {
        let fieldsWithDefaultRule = this.getSchemaFieldsWithRule('default')

        fieldsWithDefaultRule.forEach(field => {

            if(this.form().has(field)) {
                this._loadDefaultValue(field, fieldsWithDefaultRule, false)
                return // skip the rest of the code
            }

            // ----------------------------------------
            // handle reduced field names like: data.title --> data.0.title, data.1.title and etc.

            let _iterateData = ( data, parentKey = undefined ) => {
                Object.keys(data).forEach( field => {

                    let value = data[field]

                    let fieldName = parentKey === undefined
                                        ? field
                                        : parentKey + '.' + field

                    let hasArrayObjectValue = typeOf(value) === 'object' || isArrayWithObjects(value);

                    if( ! field.match(/^[0-9]$/) || ! hasArrayObjectValue ) { // skip indexes of arrays with objects
                        this._loadDefaultValue(fieldName, fieldsWithDefaultRule, true)
                    }

                    if( hasArrayObjectValue ) {
                        _iterateData( value, fieldName )
                    }
                })
            }

            field.split('.').reduce((path, current, index) => {
                let key = path
                if( path === undefined ) {
                    key = current
                }

                let value = this.form().get(key)

                if(isArrayWithObjects(value) || isObject(value)) {
                    _iterateData(value, key)
                }

                return current
            }, undefined)
        })

        return this
    }

    onError(field, callback) {
        if( typeOf(callback) !== 'function' ) {
            new Error('[Validator::onError] callback parameter need to be a function.')
        }
        this._onErrorHooks.set(field, callback)
    }

    beforeValidate(...params) {
        this._beforeValidate.forEach( action => {
            action.apply(this, params)
        })
    }

    afterValidate(...params) {
        this._afterValidate.forEach( action => {
            action.apply(this, params)
        })
    }

    _runOnErrorHooks(fieldName, errorMessage) {
        if( this._onErrorHooks.has(fieldName) ) {
            this._onErrorHooks.get(fieldName)(errorMessage)
        }
        if( this._onErrorHooks.has(null) ) {
            this._onErrorHooks.get(null)(fieldName, errorMessage)
        }
    }

    _loadDefaultValue(fieldName, fieldsWithDefaultRule, deep = true) {
        let schemaKey = fieldName

        if(deep) {
            schemaKey = false

            let schemaFields = this.findSchemaFields(fieldName)

            if( false !== schemaFields ) {
                schemaFields = schemaFields.filter(x => inArray(x, fieldsWithDefaultRule))

                if( schemaFields.length > 0 ) {
                    schemaKey = schemaFields[0]
                }
            }
        }

        if( ! inArray(schemaKey, fieldsWithDefaultRule) ) {
            return
        }

        // check if it is empty
        let ruleName = 'default'
        let rules = this.$schema[schemaKey].setValidator(this).getScenarioRules( Field.RULE_OPERATOR_AND )

        if( rules.has(ruleName) )
        {
            let ruleItem = rules.get(ruleName)

            // use the first rule isEmpty method.
            // this means that if you have some special rule.isEmpty/emptyOn logic - you need to place it as a first rule.
            let rule = ruleItem[0];

            let method = deep ? 'getDeep' : 'get'

            if(
                ! rule.isEmpty(
                    this.form()[method]( fieldName )
                )
            ) {
                return // skip apply for empty fields
            }
        }
        //

        let rulePromise = this.getRuleParam('default', schemaKey, 'value', false, true)

        if( rulePromise !== undefined ) {
            rulePromise.then(promiseResult => {
                let value = (promiseResult instanceof Function) ? promiseResult() : promiseResult
                let method = deep ? 'setDeep' : 'replace'

                // if when successful
                if ( value !== undefined ) {
                    this.form()[method](fieldName, value)
                }
            })
        }
    }

    getSchemaFieldsWithRule(ruleName) {
        let fields = []

        Object.keys(this.$schema).forEach(fieldName => {
            let rules = this.$schema[fieldName].setValidator(this).getScenarioRules( Field.RULE_OPERATOR_AND )

            if( rules.has(ruleName) ) {
                fields.push(fieldName)
            }
        })

        return fields
    }

    clearDirtyFields() {
        for ( let uniqueField of this.dirtyFields )
        {
            if( this.fieldsState[uniqueField] !== undefined ) {
                this.fieldsState[uniqueField].isDirty = false
            }
        }

        this.dirtyFields = []
        return this
    }

    registerDirtyField( field ) {
        field = this.getUniqueFieldKey(field)

        if( ! inArray(field, this.dirtyFields, true) )
        {
            this.dirtyFields.push(field)

            if ( ! this.fieldsState.hasOwnProperty(field) ) {
                this.registerFieldState(field)
            }

            this.fieldsState[field].isDirty = true
        }

        return this
    }

    unregisterDirtyField( field ) {
        field = this.getUniqueFieldKey(field)

        let index = this.dirtyFields.indexOf(field)

        if( index !== -1 )
        {
            this.dirtyFields.splice(index, 1)

            if ( ! this.fieldsState.hasOwnProperty(field) ) {
                this.registerFieldState(field)
            }

            this.fieldsState[field].isDirty = false
        }

        return this
    }

    hasDirtyField() {
        return this.dirtyFields.length > 0
    }

    isFieldDirty( field ) {
        field = this.getUniqueFieldKey(field)
        return this.fieldsState[field] !== undefined && this.fieldsState[field].isDirty
    }

    getDirtyFields() {
        return this.dirtyFields
    }

    clearLoadingFields() {
        for ( let uniqueField of this.loadingFields )
        {
            if( this.fieldsState[uniqueField] !== undefined ) {
                this.fieldsState[uniqueField].isLoading = false
            }
        }

        this.loadingFields = []

        return this
    }

    registerLoadingField( field ) {
        if( ! inArray(field, this.loadingFields, true) )
        {
            field = this.getUniqueFieldKey(field)

            if ( ! this.fieldsState.hasOwnProperty(field) ) {
                this.registerFieldState(field)
            }

            this.fieldsState[field].isLoading = true

            this.loadingFields.push(field)
        }

        return this
    }

    unregisterLoadingField( field ) {
        field = this.getUniqueFieldKey(field)

        let index = this.loadingFields.indexOf(field)

        if( index !== -1 )
        {
            if ( ! this.fieldsState.hasOwnProperty(field) ) {
                this.registerFieldState(field)
            }

            this.fieldsState[field].isLoading = false

            this.loadingFields.splice(index, 1)
        }

        return this
    }

    hasLoadingField() {
        return this.loadingFields.length > 0
    }

    isFieldLoading( field ) {
        field = this.getUniqueFieldKey(field)
        return this.fieldsState[field] !== undefined && this.fieldsState[field].isLoading
    }

    createPromiseHolder() {
        this.promiseHolder = []
    }

    form() {
        return this._form
    }

    errors( silent = false ) {
        return silent ? this.$errorsSilent : this.$errors
    }

    warnings( silent = false ) {
        return silent ? this.$warningsSilent : this.$warnings
    }

    getLanguage() {
        return this._language
    }

    setBeforeValidate( callback, key = undefined ) {
        if( key === undefined ) {
            key = this._beforeValidate.size + 1
        }
        this._beforeValidate.set(key, callback)
        return this
    }

    setAfterValidate( callback, key = undefined ) {
        if( key === undefined ) {
            key = this._afterValidate.size + 1
        }
        this._afterValidate.set(key, callback)
        return this
    }

    setLanguage( language ) {
        this._language = language
        return this
    }

    setScenario( scenario ) {
        this.scenario = scenario

        if( this.form().getOptions().useSync === true ) {
            this.sync()
        }

        return this
    }

    getScenario() {
        return this.scenario
    }

    validate( clearExistingErrors = true, silent = false ) {
        this.createPromiseHolder()

        this.warnings(silent).clear()

        if(clearExistingErrors) {
            this.errors(silent).clear()
        }

        this.beforeValidate( this )

        this._validate( this._form.all(), undefined, silent )

        return Promise.all(this.promiseHolder).then( response => {
            response.forEach( item => {
                item.forEach( field => {
                    let fieldName = field.fieldName
                    let feedbackKey = this.getUniqueFieldKey(fieldName)

                    if( field.errors.has(fieldName) ) {
                        let error = field.errors.get(fieldName)
                        this.errors(silent).add(feedbackKey, error)

                        this._runOnErrorHooks(feedbackKey, error)
                    }

                    if( field.warnings.has(fieldName) )
                        this.warnings(silent).add(feedbackKey, field.warnings.get(fieldName))
                })
            })

            let valid = ! this.errors(silent).any()

            let afterValidateResponse = this.afterValidate( valid, this )

            if ( typeof afterValidateResponse !== 'undefined' ) {
                return afterValidateResponse
            }

            return {
                valid: valid,
                errors: this.errors(silent),
                warnings: this.warnings(silent)
            }
        })
    }

    setUniqueKeyParam(param) {
        this.uniqueKeyParam = param
        return this
    }

    getUniqueKeyParam() {
        return this.uniqueKeyParam
    }

    getUniqueFieldKey(fieldName) {
        let key = fieldName

        if( false === this.getUniqueKeyParam() ) {
            return key
        }

        if( this.uniqueFieldKeyCache.hasOwnProperty(fieldName) ) {
            return this.uniqueFieldKeyCache[fieldName]
        }

        let fieldParentParentValue = this.form().getDeepParent(fieldName, undefined, -2)

        if( isArrayWithObjects(fieldParentParentValue) )
        {
            let fieldParentValue = this.form().getDeepParent(fieldName)

            if( fieldParentValue !== undefined && fieldParentValue[this.getUniqueKeyParam()] !== undefined )
            {
                let current = this._getNodeFieldName(fieldName)
                let parentFieldName = this._getParentFieldName(fieldName, '', -2)

                key = parentFieldName + '.' + fieldParentValue[this.getUniqueKeyParam()] + '.' + current
            }
        }

        this.uniqueFieldKeyCache[fieldName] = key

        return key
    }

    validateField( fieldName, preserveExistingErrors = true, silent = false ) {

        let feedbackKey = this.getUniqueFieldKey(fieldName)

        this.warnings(silent).remove(feedbackKey)

        if(false === preserveExistingErrors) {
            this.errors(silent).remove(feedbackKey)
        }

        this.beforeValidate( this, fieldName )

        let fieldValue = this.form().getDeep(fieldName)

        return this._validateField(fieldName, fieldValue, silent).then( response => {
            response.forEach( field => {
                let fieldName = field.fieldName

                if( field.errors.has(fieldName) ) {
                    let error = field.errors.get(fieldName)

                    this.errors(silent).add(feedbackKey, error)

                    this._runOnErrorHooks(feedbackKey, error)
                }

                if( field.warnings.has(fieldName) )
                    this.warnings(silent).add(feedbackKey, field.warnings.get(fieldName))
            })

            let afterValidateResponse = this.afterValidate( response, this, fieldName )

            if ( typeof afterValidateResponse !== 'undefined' ) {
                return afterValidateResponse
            }

            return {
                valid: ! this.errors(silent).has( feedbackKey ),
                errors: this.errors(silent).get( feedbackKey ),
                warnings: this.warnings(silent).get( feedbackKey )
            }
        })
    }

    isRequired( field ) {
        return this._getFieldAttributeState(field, 'isRequired')
    }

    isDisabled( field ) {
        return this._getFieldAttributeState(field, 'isDisabled')
    }

    isReadonly( field ) {
        return this._getFieldAttributeState(field, 'isReadonly')
    }

    isVisible( field ) {
        return this._getFieldAttributeState(field, 'isVisible')
    }

    findFirstSchemaField( fieldName ) {
        return this.findSchemaFields( fieldName, true )
    }

    findSchemaFields( fieldName, firstOnly = false, container = undefined ) {

        if( undefined === container ) {
            container = this.$schema
        }

        let match = new Set()

        // match: item.0.id
        if( container[fieldName] !== undefined ) {
            match.add(fieldName)
        }

        // match: item.id
        let schemaFieldName = this._getSchemaFieldName(fieldName)
        if( container[schemaFieldName] !== undefined ) {
            match.add(schemaFieldName)
        }

        // match: item.*
        let asteriskPath = this._getAsteriskPath(schemaFieldName)

        if( container[asteriskPath] !== undefined ) {
            match.add(asteriskPath)
        }

        if( match.size > 0 && firstOnly ) {
            return Array.from(match)[0]
        }

        // do more complicated match
        for( let schemaKey in container ) {
            // case: regex $(/0-9/) or $(new RegExp('0-9'))
            if( schemaKey.indexOf('/') === 0 ) {
                let lastSlash = schemaKey.lastIndexOf("/");
                let regex = new RegExp(schemaKey.slice(1, lastSlash), schemaKey.slice(lastSlash + 1));

                if( regex.test(String(fieldName)) ) {
                    match.add(schemaKey)
                }
            }
        }

        if( match.size > 0 && firstOnly ) {
            return Array.from(match)[0]
        }

        if( match.size === 0 ) {
            return false
        }

        return Array.from(match)
    }

    _getFieldAttributeState ( field, attribute ) {
        let schemaFields = this.findSchemaFields(field)

        if( false === schemaFields ) {
            return (attribute === 'isVisible') ? true : false
        }

        for( let i in schemaFields )
        {
            if( this.$schema[schemaFields[i]].setValidator(this)[attribute]( field ) ) {
                return true
            }
        }

        return false
    }

    getRuleParam( rule, field, param, instantly = false, asPromise = false ) {
        let schemaField = this.findFirstSchemaField(field)

        if( false === schemaField ) {
            return
        }

        let method = asPromise ? 'getRuleParamPromise' : 'getRuleParam'

        return this.$schema[schemaField].setValidator(this)[method]( rule, field, param, instantly )
    }

    registerFields( fields ) {
        fields.forEach( field => {
            if( typeof this.$schema[field] === 'undefined' ) {
                this.$schema[field] = new Field(field)
            }
        })

        return this
    }

    setRules( rules, fieldsPrefix = null ) {
        this.$schema = new RulesParser().load(this.$schema).setFieldsPrefix(fieldsPrefix).parse(rules).asSchema()
        return this
    }

    unsetRule( field ) {
        delete this.$schema[field]
        return this
    }

    /**
     * Vaidate field against specific validator rule.
     *
     * @param  {String} validatorName Rule validation name.
     * @param  {String} fieldName     Name/path of the field that will be validated.
     * @param  {mixed}  value         Validaiton field value.
     * @param  {Object} [params={}]   Rule params.
     * @param  {Object} rule          FieldRule instance of the current rule
     *
     * @return {Boolean|Object}       The result may be `true` or `false`, but also
     *                                an object `{ result: Boolean, message: String }`.
     *                                The object is used in case of multiple error messages per validator.
     */
    runValidation( validatorName, fieldName, value, params = {}, rule ) {
        if( typeof this.$validators[validatorName] === 'undefined' ) {
            return Promise.reject(new Error('[Validator]: Invalid validator name "'+ validatorName +'" for field "'+ fieldName +'".'))
        }

        return Promise.resolve(this.$validators[validatorName].bind(this)(value, params, this, fieldName, rule))
    }

    //////////////////////////////////////////////////

    _getSchemaFieldName( fieldName ) {
        return String(fieldName).replace(/\.[0-9]+\./gi, '.')
    }

    _getAsteriskPath( schemaFieldName ) {
        if( schemaFieldName.lastIndexOf('.') === -1 )
            return '*'

        return this._getParentFieldName(schemaFieldName) + '.*'
    }

    _getParentFieldName(fieldName, otherwise = '', parentLevel = -1) {
        let parentKey = substringIndex(fieldName, '.', parentLevel)

        if( parentKey === '' ) {
            return otherwise
        }

        return parentKey
    }

    _getNodeFieldName(fieldName) {
        let index = fieldName.lastIndexOf('.')

        if( index !== -1 ) {
            index++
        } else {
            index
        }

        return fieldName.substring(index)
    }

    _validate( data, parentKey = undefined, silent = false ) {
        Object.keys(data).forEach( field => {

            let value = data[field]

            let fieldName = parentKey === undefined
                                ? field
                                : parentKey + '.' + field

            let hasArrayObjectValue = typeOf(value) === 'object' || isArrayWithObjects(value)

            if( ! field.match(/^[0-9]$/) || ! hasArrayObjectValue ) { // skip validation of indexes of arrays with objects
                this.promiseHolder.push(
                    this._validateField(fieldName, value, silent)
                )
            }

            if( hasArrayObjectValue ) {
                this._validate( value, fieldName, silent )
            }
        })
    }

    _validateField( fieldName, value, silent = false ) {
        if( silent === false ) {
            this.registerDirtyField( fieldName )
            this.registerLoadingField( fieldName )
        }

        let promiseHolder = []

        let schemaFields = this.findSchemaFields(fieldName)

        if( false !== schemaFields ) {
            for( let i in schemaFields ) {
                promiseHolder.push(this.$schema[schemaFields[i]].setValidator( this ).validate( fieldName, value ))
            }
        }

        return Promise.all( promiseHolder ).then( response => {
            if( silent === false ) {
                this.unregisterLoadingField( fieldName )
            }
            return response
        })
    }

    dump(forCurrentScenarioOnly = true)
    {
        console.log("%c--- Schema Field Rules ---", "color: Coral; font-size: 16px")
        console.log("Current scenario:")
        console.log("%c" + this.getScenario(), "color: #E55451; font-size: 14px;")
        console.log("Show current scenario rules only:")
        console.log("%c" + (forCurrentScenarioOnly ? "yes" : "no"), "color: #E55451; font-size: 14px;")
        console.log("%c--------------------------", "color: Coral; font-size: 16px")

        for( let field in this.$schema )
        {
            console.groupCollapsed("%c" + field, "color: #AFDCEC;")

            for( let scenario in this.$schema[field].rules )
            {
                if( forCurrentScenarioOnly && this.getScenario() !== scenario ) {
                    continue
                }

                console.group(scenario)

                for( let operator in this.$schema[field].rules[scenario] )
                {
                    console.group(operator)

                    let rulesMap = this.$schema[field].rules[scenario][operator]

                    rulesMap.forEach( (rule, ruleName) => {
                        console.group(ruleName)

                        if( isArray(rule) ) {
                            for( let ruleIndex in rule ) {
                                console.info(rule[ruleIndex])
                            }
                        } else {
                            console.info(rule)
                        }

                        console.groupEnd(ruleName)
                    })

                    console.groupEnd(operator)
                }

                console.groupEnd(scenario)
            }

            console.groupEnd(field)
        }

        console.log("%c--------------------------", "color: Coral; font-size: 16px")
    }
}
